import LineItem from '~/types/LineItem';
import Order, {
  calculateValueByQuantity,
  isFulfilled,
  isFulfilledInShippoOldExperience,
  isFulfilledOutsideShippo,
  isOrderLineItemEditable,
  isPartialFulfillmentAllCompleted,
  OrderSelection,
  OrdersRenderData,
} from '~/types/Order';
import { BatchEntryExtended, LineItemLite } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import Shipment from '~/types/Shipment';
import { Transaction, TransactionStatus } from '~/types/Transaction';
import * as Sentry from '@sentry/browser';

export function getOldExperienceTransaction(
  transactions: Transaction[],
  batchEntry?: BatchEntryExtended,
) {
  return batchEntry?.transactionObjectId
    ? transactions.find((t) => t.objectId === batchEntry.transactionObjectId)
    : transactions[transactions.length - 1];
}

export function getIsFulfilledWithCompleteOrErrorBatchEntry(
  order: Order,
  batchEntry?: BatchEntryExtended,
) {
  return !!(
    isFulfilled(order) &&
    batchEntry &&
    ![
      BatchEntryStatus.COMPLETE_SHIPMENT,
      BatchEntryStatus.PENDING,
      BatchEntryStatus.PROCESSING_SHIPMENT,
    ].includes(batchEntry.objectStatus) &&
    batchEntry.partialFulfillmentRank === 0
  );
}

export function getShouldRenderOldExperienceData(order: Order, batchEntry?: BatchEntryExtended) {
  const isFulfilledWithCompleteOrErrorBatchEntry = getIsFulfilledWithCompleteOrErrorBatchEntry(
    order,
    batchEntry,
  );

  return (
    ((isFulfilledInShippoOldExperience(order) && !batchEntry) ||
      isFulfilledWithCompleteOrErrorBatchEntry) &&
    !isFulfilledOutsideShippo(order)
  );
}

export function overlyComplicatedTrackingNumberSelection(trackingNumber: string) {
  const textArea = document.createElement('textarea');
  textArea.textContent = trackingNumber;
  document.body.appendChild(textArea);
  const selection = document.getSelection();
  const range = document.createRange();
  range.selectNode(textArea);
  selection?.removeAllRanges();
  selection?.addRange(range);
  document.execCommand('copy');
  selection?.removeAllRanges();
  document.body.removeChild(textArea);
}

export function noRatesErrorState(
  batchEntry: BatchEntryExtended | null | undefined,
  shipment: Shipment | null | undefined,
): shipment is Shipment {
  return !!(
    batchEntry &&
    batchEntry.objectStatus === BatchEntryStatus.COMPLETE_SHIPMENT &&
    shipment &&
    shipment.rates.length === 0 &&
    shipment.messages.length > 0
  );
}

export function isPartialParentRow(ordersRenderData: OrdersRenderData) {
  return ordersRenderData.kind === 'partial-parent';
}

export function isRowEntryFulfilled(
  order: Order,
  partialParentRow: boolean,
  batchEntry?: BatchEntryExtended,
) {
  const hasCompleteBatchEntry =
    batchEntry && batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION;

  const isPartialParentFulfilled = partialParentRow && isPartialFulfillmentAllCompleted(order);

  const isEntryFulfilled =
    isPartialParentFulfilled ||
    isFulfilledInShippoOldExperience(order) ||
    (isFulfilled(order) && hasCompleteBatchEntry);

  return isEntryFulfilled;
}

export function getSelectedOrdersWithLabels(
  selectedOrders: OrderSelection,
  ordersData: { [orderId: string]: Order },
  batchEntries: HashMap<number, BatchEntryExtended>,
) {
  return Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, batchEntryIds]) => {
    if (batchEntryIds.length > 0) {
      const beIdsWithTransaction = batchEntryIds.filter(
        (beId) => !!batchEntries[beId]?.transactionObjectId,
      );

      // No batch entries have transactions, ignore any possible transactions in order
      if (beIdsWithTransaction.length === 0) {
        return obj;
      }

      return { ...obj, [orderId]: beIdsWithTransaction };
    }

    // No batch entries but has transaction
    if (ordersData[orderId]?.transactions.length > 0) {
      return { ...obj, [orderId]: [] };
    }

    return obj;
  }, {});
}

/**
 * This method is like the previous method but this
 * goes one level deeper and check if the transaction
 * actually has a label and not a failed transaction
 * @param selectedOrders
 * @param ordersData
 * @param batchEntries
 * @returns
 */
export function getSelectedOrdersWithSuccessfulTransaction(
  selectedOrders: OrderSelection,
  ordersData: { [orderId: string]: Order },
  batchEntries: HashMap<number, BatchEntryExtended>,
): OrderSelection {
  return Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, batchEntryIds]) => {
    if (batchEntryIds.length > 0) {
      const beIdsWithTransaction = batchEntryIds.filter((beId) => {
        const transactionObjectID = batchEntries[beId]?.transactionObjectId;
        const transaction = ordersData[orderId]?.transactions.find(
          (t) => t.objectId === transactionObjectID,
        );
        return transaction?.status === TransactionStatus.SUCCESS;
      });

      // No batch entries have transactions, ignore any possible transactions in order
      if (beIdsWithTransaction.length === 0) {
        return obj;
      }

      return { ...obj, [orderId]: beIdsWithTransaction };
    }

    // No batch entries but has transaction
    if (
      ordersData[orderId]?.transactions.filter((t) => t.status === TransactionStatus.SUCCESS)
        .length > 0
    ) {
      return { ...obj, [orderId]: [] };
    }

    return obj;
  }, {});
}

export function lineItemsFromBatchEntry(order: Order, batchEntry?: BatchEntryExtended): LineItem[] {
  if (!batchEntry || batchEntry.lineItems.length === 0) {
    return order.lineItems;
  }

  const orderLineItemMap = order.lineItems.reduce<Record<string, LineItem>>((accum, li) => {
    accum[li.objectId] = li;
    return accum;
  }, {});

  const isLineItemOnOrderMapFilter = (lineItem: LineItemLite) => {
    const isLineItemOnOrderMap = orderLineItemMap[lineItem.objectId];

    if (!isLineItemOnOrderMap) {
      Sentry.captureException(
        new Error(`Missing line item ${lineItem.objectId} on batch entry: ${batchEntry.id}`),
      );
    }

    return isLineItemOnOrderMap;
  };

  return batchEntry.lineItems
    .filter(isLineItemOnOrderMapFilter)
    .reduce((list: LineItem[], lineItemLite) => {
      const lineItem = orderLineItemMap[lineItemLite.objectId];
      const { quantity } = lineItemLite;

      list.push({
        ...lineItem,
        quantity,
        totalAmount: calculateValueByQuantity(lineItem.unitAmount, quantity),
        totalWeight: calculateValueByQuantity(lineItem.unitWeight, quantity),
      });

      return list;
    }, []);
}

export function editItemsLinkTarget(order?: Order) {
  if (!order || !isOrderLineItemEditable(order)) {
    return;
  }

  return `/orders/${order.objectId}`;
}
